import { Component, Host, Prop, State, Watch, h } from '@stencil/core';
import { renderTools } from '../../helpers/RenderTools';
import { ImageParams } from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-youtube-video',
  styleUrl: 'cms-youtube-video.scss',
  shadow: true,
})
export class CmsYoutubeVideo {
  private imageParams: ImageParams = {
    responsiveImages: null,
    imageSizes: null,
    altText: null,
    imageClass: null,
    imageStyles: null,
    lazyLoad: true,
  };

  private imageSizes = `
  (min-width: 768px) and (max-width: 1440px) and (min-resolution: 288dpi) 50vw,
  (min-width: 768px) and (max-width: 1440px) and (min-resolution: 192dpi) 50vw,
  (min-width: 768px) and (max-width: 1440px) and (min-resolution: 96dpi) 50vw,
  (min-width: 1440px) 700px,
  100vw`;

  private consentButtonClassName = 'hdp-btn-accept';

  private videoUrlConfig = {
    youTubeBaseUrl: 'https://www.youtube-nocookie.com/embed/',
    listSuffix: 'videoseries?',
    noRecommendedVidsPostfix: 'rel=0',
    autoPlay: 'autoplay=1',
    showInfo: 'showinfo=0',
  };

  private videoStyles = {
    aspectRatio: '1.7 / 1',
    width: '100%',
    height: 'auto',
  };

  @State() loadVideo = false;
  @State() showDialog: boolean | undefined;

  /**
   * set the usercentrics consent status
   */
  @Prop({ reflect: true }) isConsent: boolean;

  /**
   * set the autoplay parameter from outside to start video automatically
   */
  @Prop({ reflect: true }) autostart: boolean;

  /**
   * Set the youtube video ID
   */
  @Prop() videoParam: string;

  /**
   * Set the youtube video title
   */
  @Prop() videoTitle: string;

  /**
   * set lazyLoad parameter important for keyVisual
   */
  @Prop() lazyLoad: boolean;

  @Watch('isConsent')
  onCheckedChange(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      this.updateVideoState();
    }
  }

  private clickHandle(event: Event) {
    const { target } = event;
    const { className } = target as HTMLElement;

    if (className === this.consentButtonClassName) {
      this.showDialog = false;
      this.loadVideo = true;
    } else if (this.isConsent) {
      this.loadVideo = true;
      this.showDialog = false;
    } else {
      this.showDialog = true;
    }

    this.updateVideoState();
  }

  private updateVideoState() {
    if (this.isConsent) {
      this.showDialog = false;
    } else {
      this.loadVideo = false;
    }
  }

  private renderContent() {
    if (this.loadVideo) {
      return this.renderVideo();
    }
    if (!this.loadVideo && !this.showDialog) {
      return this.renderImage();
    }
  }

  private renderImage() {
    const image = renderTools.renderYoutubeImages(this.imageParams);
    return (
      <div
        onClick={(event: Event) => {
          this.clickHandle(event);
        }}
        class={'video-image-wrapper'}
      >
        {image}
      </div>
    );
  }

  private renderVideo() {
    return (
      <iframe
        width='750'
        height='450'
        loading={this.lazyLoad ? 'lazy' : 'eager'}
        style={this.videoStyles}
        title={this.videoTitle}
        class={'youtube-video'}
        allowFullScreen
        src={this.buildVideoUrl()}
      />
    );
  }

  private buildVideoUrl() {
    const baseParams = `${this.videoUrlConfig.autoPlay}&mute=1&${this.videoUrlConfig.noRecommendedVidsPostfix}&${this.videoUrlConfig.showInfo}`;
    const videoBaseUrl = this.videoUrlConfig.youTubeBaseUrl;
    const videoIdOrList = this.hasPlayList()
      ? `${this.videoUrlConfig.listSuffix}${this.videoParam}`
      : this.videoParam;

    return `${videoBaseUrl}${videoIdOrList}${
      this.hasPlayList() ? '&' : '?'
    }${baseParams}`;
  }

  private getYouTubeImage() {
    return this.videoParam
      ? renderTools.generateYoutubeImages(this.getId(this.videoParam))
      : null;
  }

  private getId(youTubeParam: string) {
    const splitParam = youTubeParam?.split('&')[0].split('?');
    return splitParam[0];
  }

  private hasPlayList() {
    return this.videoParam?.includes('list=');
  }

  componentWillLoad() {
    this.imageParams = {
      ...this.imageParams,
      responsiveImages: this.getYouTubeImage(),
      imageSizes: this.imageSizes,
      altText: this.videoTitle || 'Hornbach YouTube Video',
      imageClass: 'youtube-video-image',
      lazyLoad: this.lazyLoad,
    };
    if (this.autostart) {
      this.loadVideo = true;
    }
  }

  render() {
    return (
      <Host>
        <div class={'youtube-container'}>
          {this.showDialog ? (
            <div
              onClick={(event) => {
                this.clickHandle(event);
              }}
              class='consent-container'
            >
              <slot name='hdp-consent' />
            </div>
          ) : null}

          <div class={'youtube-video-wrapper'}>{this.renderContent()}</div>
        </div>
      </Host>
    );
  }
}
